// src/utils/teachingDiscipline.js
//
// Pass 83B §24–§26 — the season-scoped INTERNAL teaching-discipline capability used for lesson-assignment
// eligibility. This is the CANONICAL operational source (a net-new field
// `seasonMemberships.<seasonId>.teachingDisciplines` = ['ski','snowboard'], mirroring `workCapabilities`).
// The DRAFT `instructorPublicProfiles.disciplines` is NEVER the authoritative assignment source — profiles
// are a separate, publishable, human-editable surface. This capability is populated from the SAME accepted
// Pass 83A roster evidence via a controlled idempotent apply script (no keyword parsing of booking text).
//
// Transition-safe, exactly like `memberServesArea` (operatingAreas.js): a member with NO disciplines set is
// treated as UNKNOWN and is NOT excluded, so wiring this in never silently empties the candidate pool before
// the backfill has run for every human.

export const TEACHING_DISCIPLINE = { SKI: 'ski', SNOWBOARD: 'snowboard' };
export const TEACHING_DISCIPLINES = [TEACHING_DISCIPLINE.SKI, TEACHING_DISCIPLINE.SNOWBOARD];

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

// Normalize a raw discipline token to a canonical teaching discipline id, else '' (unknown/not-a-discipline).
export function canonicalTeachingDiscipline(value) {
  const v = norm(value);
  if (v === 'ski' || v === 'skiing' || v === 'skier') return TEACHING_DISCIPLINE.SKI;
  if (v === 'snowboard' || v === 'snowboarding' || v === 'board' || v === 'snb') return TEACHING_DISCIPLINE.SNOWBOARD;
  return '';
}

// The member's season-scoped teaching disciplines (canonical, deduped). [] when none are set.
export function memberTeachingDisciplines(member = {}, seasonId = '') {
  const season = seasonId && member && member.seasonMemberships ? member.seasonMemberships[seasonId] : null;
  const raw = Array.isArray(season && season.teachingDisciplines) ? season.teachingDisciplines : [];
  const out = [];
  for (const d of raw) { const c = canonicalTeachingDiscipline(d); if (c && !out.includes(c)) out.push(c); }
  return out;
}

// The discipline a booking REQUIRES for teaching assignment. Only a client teaching booking carries a
// meaningful discipline; a booking with no explicit/known discipline returns '' → no discipline gate (§26).
export function bookingTeachingDiscipline(booking = {}) {
  return canonicalTeachingDiscipline(booking && booking.discipline);
}

// Transition-safe eligibility: true when no discipline is required, OR the member has NO disciplines set
// (unknown → not excluded), OR the member teaches the required discipline.
export function memberTeachesDiscipline(member = {}, discipline = '', seasonId = '') {
  const need = canonicalTeachingDiscipline(discipline);
  if (!need) return true;
  const have = memberTeachingDisciplines(member, seasonId);
  if (have.length === 0) return true;
  return have.includes(need);
}

// Filter assignment candidates by a booking's required teaching discipline. No-op when the discipline is
// unset/unknown, so existing (disciplineless) behaviour is preserved (§26).
export function filterDisciplineCandidates(candidates = [], { discipline = '', seasonId = '' } = {}) {
  const need = canonicalTeachingDiscipline(discipline);
  if (!need) return candidates;
  return candidates.filter((m) => memberTeachesDiscipline(m, need, seasonId));
}
